/**
 * Canvas → Blob encoding + encode-format detection.
 *
 * `canvasToBlob` unifies the promisified-`toBlob` wrappers that were
 * copy-pasted across image-editor slice export, litsquad's avatar editor,
 * and travel's trip export. The two behaviors every copy shared are the
 * contract here:
 *
 *  - The encoder returning `null` (unsupported format, zero-sized canvas,
 *    some tainted-canvas paths) REJECTS with a named `CanvasEncodeError` so
 *    callers can distinguish encode failure from everything else. Soft-fail
 *    callers `.catch` on `instanceof CanvasEncodeError`.
 *  - A SYNCHRONOUS `toBlob` throw (per the WHATWG spec a tainted canvas
 *    throws `SecurityError` synchronously) is NOT caught or wrapped — the
 *    promise rejects with the original error, preserving its identity for
 *    callers that branch on `DOMException`.
 */

/** `canvas.toBlob` returned `null` — the encode itself failed. */
export class CanvasEncodeError extends Error {
  /** The MIME type the encode was attempted with. */
  readonly mimeType: string;

  constructor(mimeType: string) {
    super(
      `Canvas could not be exported as ${mimeType} ` +
        `(toBlob returned null — likely CORS-tainted, zero-sized, or an unsupported format)`,
    );
    this.name = 'CanvasEncodeError';
    this.mimeType = mimeType;
  }
}

/**
 * Promise wrapper around `HTMLCanvasElement.toBlob`.
 *
 * `quality` is forwarded verbatim — including for lossless formats like
 * PNG, whose encoders ignore it — so refactored call sites hand the
 * encoder bit-identical inputs. It only affects lossy encoders
 * (`image/jpeg`, `image/webp`, `image/avif` where supported).
 */
export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string = 'image/png',
  quality?: number,
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new CanvasEncodeError(mimeType));
      },
      mimeType,
      quality,
    );
  });
}

/**
 * Whether the host browser can ENCODE the given image MIME type via the
 * canvas API (e.g. `'image/avif'`, `'image/webp'`).
 *
 * Browsers silently fall back to PNG inside `toDataURL` for unrecognized
 * encode MIMEs, so the data-URL prefix check is the standard synchronous
 * support probe. The probe canvas is explicitly 1×1 — never encode the
 * default 300×150 bitmap just to sniff a format.
 *
 * Deliberately NOT cached here: callers that want module-load-once
 * semantics keep their own `const SUPPORTS_X = supportsImageFormat(...)`.
 * (Keeping this stateless also keeps `vi.resetModules()`-based tests
 * honest — they re-probe whatever prototype is currently patched.)
 *
 * Both `typeof window` AND `typeof document` are checked because some
 * SSR-adjacent environments (Vite SSR mode, node-with-jsdom test runners)
 * shim one but not the other.
 */
export function supportsImageFormat(mimeType: string): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  // Fully fail-soft: a non-string (type-erased caller) returns false rather
  // than throwing from the normalization step (M5 post-commit review #4).
  if (typeof mimeType !== 'string') return false;
  const mime = mimeType.toLowerCase();
  try {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    if (typeof probe.toDataURL !== 'function') return false;
    return probe.toDataURL(mime).startsWith(`data:${mime}`);
  } catch {
    return false;
  }
}
